import {
  WorkspaceMemberRole,
  type WorkspaceMemberWithUserDto,
  type WorkspaceRoleListItemDto,
} from '@up2stay/up2promo-api-client';

export type RoleAssignment =
  | { kind: 'builtin'; role: WorkspaceMemberRole }
  | { kind: 'custom'; customRoleId: string };

export function roleAssignmentToValue(assignment: RoleAssignment): string {
  if (assignment.kind === 'builtin') {
    return `builtin:${assignment.role}`;
  }
  return `custom:${assignment.customRoleId}`;
}

export function roleAssignmentFromValue(value: string): RoleAssignment | null {
  if (value.startsWith('builtin:')) {
    const role = value.slice('builtin:'.length) as WorkspaceMemberRole;
    if (!Object.values(WorkspaceMemberRole).includes(role)) return null;
    return { kind: 'builtin', role };
  }
  if (value.startsWith('custom:')) {
    const customRoleId = value.slice('custom:'.length);
    if (!customRoleId) return null;
    return { kind: 'custom', customRoleId };
  }
  return null;
}

export function memberToRoleAssignment(
  member: WorkspaceMemberWithUserDto,
): RoleAssignment {
  if (member.customRoleId?.trim()) {
    return { kind: 'custom', customRoleId: member.customRoleId };
  }
  return { kind: 'builtin', role: member.role };
}

export function resolveRoleListItemName(
  role: WorkspaceRoleListItemDto,
  builtinLabels: Record<WorkspaceMemberRole, string>,
): string {
  if (role.isSystem && role.builtinRole) {
    return builtinLabels[role.builtinRole] ?? role.name;
  }
  return role.name;
}

export function resolveRoleListItemDescription(
  role: WorkspaceRoleListItemDto,
  builtinDescriptions: Partial<Record<WorkspaceMemberRole, string>>,
): string | undefined {
  if (role.description?.trim()) {
    return role.description;
  }
  if (role.isSystem && role.builtinRole) {
    return builtinDescriptions[role.builtinRole];
  }
  return undefined;
}

export function resolveRoleDisplayName(
  assignment: RoleAssignment,
  roles: WorkspaceRoleListItemDto[],
  builtinLabels: Record<WorkspaceMemberRole, string>,
): string {
  if (assignment.kind === 'builtin') {
    return builtinLabels[assignment.role] ?? assignment.role;
  }
  const custom = roles.find((r) => r.id === assignment.customRoleId);
  return custom?.name ?? assignment.customRoleId;
}

export function assignmentsEqual(
  a: RoleAssignment,
  b: RoleAssignment,
): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'builtin' && b.kind === 'builtin') {
    return a.role === b.role;
  }
  if (a.kind === 'custom' && b.kind === 'custom') {
    return a.customRoleId === b.customRoleId;
  }
  return false;
}
